import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import styles from './Profile.module.css';
import api from '../services/api';
import {
  UserIcon,
  BookIcon,
  TargetIcon,
  SettingsIcon,
  MessageIcon,
  InfoIcon,
  StarIcon,
  HeartIcon,
  MoonIcon,
  SunIcon,
  HistoryIcon,
  TrophyIcon,
} from '../components/Icons';

const Profile = () => {
  const { user: authUser, logout } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const [userStats, setUserStats] = useState({
    favoriteWords: 0,
    favoriteSentences: 0,
    readChapters: 0,
    totalWords: 0
  });
  const [loading, setLoading] = useState(true);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [copyStatus, setCopyStatus] = useState('');
  const [vipExpiresAt, setVipExpiresAt] = useState(null);

  // 获取用户统计数据
  useEffect(() => {
    const fetchUserStats = async () => {
      try {
        setLoading(true);
        // 这里可以添加API调用来获取用户统计数据
        // 暂时使用模拟数据
        setUserStats({
          favoriteWords: 25,
          favoriteSentences: 12,
          readChapters: 8,
          totalWords: 156
        });

        // 获取用户邀请码
        const response = await api.get('/auth/me');
        const code = response?.data?.invite_code;
        const vipExpiry = response?.data?.vip_expires_at;
        if (code) {
          setInviteCode(code);
          const url = new URL(window.location.origin);
          // 假设注册页路径为 /register（根据实际情况调整）
          const inviteUrl = `${url.origin}/register?invite=${code}`;
          setInviteLink(inviteUrl);
        } else {
          setInviteCode('');
          setInviteLink('');
        }
        if (vipExpiry) {
          setVipExpiresAt(new Date(vipExpiry));
        } else {
          setVipExpiresAt(null);
        }
      } catch (error) {
        console.error('获取用户统计失败:', error);
      } finally {
        setLoading(false);
      }
    };

    if (authUser) {
      fetchUserStats();
    }
  }, [authUser]);

  // 处理菜单项点击
  const handleMenuClick = (action) => {
    switch (action) {
      case 'feedback':
        navigate('/feedback');
        break;
      case 'about':
        navigate('/about');
        break;
      case 'settings':
        alert('设置功能开发中...');
        break;
      default:
        break;
    }
  };

  const handleCopy = async (value, label) => {
    if (!value) return;
    if (!navigator?.clipboard) {
      setCopyStatus('浏览器不支持复制功能');
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      setCopyStatus(`${label}已复制`);
      setTimeout(() => setCopyStatus(''), 2000);
    } catch (error) {
      console.error('复制失败:', error);
      setCopyStatus('复制失败，请稍后重试');
      setTimeout(() => setCopyStatus(''), 2000);
    }
  };

  const renderVipStatus = () => {
    if (!vipExpiresAt) {
      return <span className={styles.vipInactive}>您还不是 VIP</span>;
    }

    const formatter = new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    return (
      <span className={styles.vipActive}>
        VIP 有效期至 {formatter.format(vipExpiresAt)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className={styles.profileLoading}>
        <div>加载中...</div>
      </div>
    );
  }

  return (
    <div className={styles.profile}>
      {/* 我的账户 */}
      <section className={styles.accountSection}>
 
        <div className={styles.accountCard}>
          <div className={styles.userSummary}>
            <div className={styles.userAvatar}>
              <UserIcon size={40} />
            </div>
            <div className={styles.userInfo}>
              <h3 className={styles.username}>{authUser?.username || '用户'}</h3>
              <div className={styles.vipStatus}>{renderVipStatus()}</div>
            </div>
          </div>
        </div>
      </section>

      {/* 邀请信息 */}
      <div className={styles.inviteCard}>
        <div className={styles.inviteHeader}>
          <div>分享邀请码，您和被分享者免费获得20天vip</div>
          {copyStatus && <span className={styles.copyStatus}>{copyStatus}</span>}
        </div>
        {inviteCode ? (
          <>
            <div className={styles.inviteRow}>
              {/* <span className={styles.inviteLabel}>邀请码</span> */}
              <div className={styles.inviteValueWrapper}>
                <span className={styles.inviteCode}>{inviteCode}</span>
                <button
                  type="button"
                  className={styles.copyBtn}
                  onClick={() => handleCopy(inviteCode, '邀请码')}
                >
                  复制
                </button>
              </div>
            </div>
            {inviteLink && (
              <div className={styles.inviteRow}>
                <span className={styles.inviteLabel}>分享链接</span>
                <div className={styles.inviteValueWrapper}>
                  <span className={styles.inviteLink}>{inviteLink}</span>
                  <button
                    type="button"
                    className={styles.copyBtn}
                    onClick={() => handleCopy(inviteLink, '邀请链接')}
                  >
                    复制
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <p className={styles.invitePlaceholder}>暂未生成邀请码，请稍后重试或联系客服</p>
        )}
      </div>

      {/* 功能菜单 */}
      <div className={styles.menuSection}>
        <div className={styles.menuList}>
          <div 
            className={styles.menuItem}
            onClick={() => navigate('/profile/account')}
          >
            <div className={styles.menuIcon}>
              <UserIcon size={24} />
            </div>
            <div className={styles.menuContent}>
              <div className={styles.menuTitle}>我的账户</div>
              <div className={styles.menuDescription}>查看账号信息与安全设置</div>
            </div>
            <div className={styles.menuArrow}>›</div>
          </div>

          {/* 主题切换 */}
          <div 
            className={styles.menuItem}
            onClick={toggleTheme}
          >
            <div className={styles.menuIcon}>
              {isDark ? <SunIcon size={24} /> : <MoonIcon size={24} />}
            </div>
            <div className={styles.menuContent}>
              <div className={styles.menuTitle}>
                {isDark ? '浅色模式' : '夜晚模式'}
              </div>
              <div className={styles.menuDescription}>
                {isDark ? '切换到浅色主题' : '切换到深色主题'}
              </div>
            </div>
            <div className={styles.menuArrow}>›</div>
          </div>

          <div 
            className={styles.menuItem}
            onClick={() => navigate('/profile/history')}
          >
            <div className={styles.menuIcon}>
              <HistoryIcon size={24} />
            </div>
            <div className={styles.menuContent}>
              <div className={styles.menuTitle}>阅读历史</div>
              <div className={styles.menuDescription}>查看章节阅读记录</div>
            </div>
            <div className={styles.menuArrow}>›</div>
          </div>

          <div 
            className={styles.menuItem}
            onClick={() => navigate('/novel-voting')}
          >
            <div className={styles.menuIcon}>
              <TrophyIcon size={24} />
            </div>
            <div className={styles.menuContent}>
              <div className={styles.menuTitle}>小说投票</div>
              <div className={styles.menuDescription}>为您喜欢的小说投票</div>
            </div>
            <div className={styles.menuArrow}>›</div>
          </div>

          <div 
            className={styles.menuItem}
            onClick={() => handleMenuClick('feedback')}
          >
            <div className={styles.menuIcon}>
              <MessageIcon size={24} />
            </div>
            <div className={styles.menuContent}>
              <div className={styles.menuTitle}>用户反馈</div>
              <div className={styles.menuDescription}>提交建议和问题反馈</div>
            </div>
            <div className={styles.menuArrow}>›</div>
          </div>

          <div 
            className={styles.menuItem}
            onClick={() => handleMenuClick('about')}
          >
            <div className={styles.menuIcon}>
              <InfoIcon size={24} />
            </div>
            <div className={styles.menuContent}>
              <div className={styles.menuTitle}>关于我们</div>
              <div className={styles.menuDescription}>了解应用版本和团队信息</div>
            </div>
            <div className={styles.menuArrow}>›</div>
          </div>

          <div 
            className={styles.menuItem}
            onClick={() => handleMenuClick('settings')}
          >
            <div className={styles.menuIcon}>
              <SettingsIcon size={24} />
            </div>
            <div className={styles.menuContent}>
              <div className={styles.menuTitle}>应用设置</div>
              <div className={styles.menuDescription}>个性化设置和偏好配置</div>
            </div>
            <div className={styles.menuArrow}>›</div>
          </div>

          <div 
            className={styles.menuItem}
            onClick={() => navigate('/help')}
          >
            <div className={styles.menuIcon}>
              <BookIcon size={24} />
            </div>
            <div className={styles.menuContent}>
              <div className={styles.menuTitle}>帮助中心</div>
              <div className={styles.menuDescription}>使用指南和常见问题</div>
            </div>
            <div className={styles.menuArrow}>›</div>
          </div>
        </div>
      </div>

      {/* 退出登录 */}
      <div className={styles.logoutSection}>
        <button 
          className={`${styles.logoutBtn} btn btn-secondary`}
          onClick={logout}
        >
          退出登录
        </button>
      </div>
    </div>
  );
};

export default Profile;
